import { Clear as ClearIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Menu,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { GlobalSearchResult } from 'GlobalSearchResult';
import React from 'react';
import { activityLogEventConfig } from '../../models/ActivityLog';
import { Palette } from 'Palette';
import { RADIUS } from 'DesignTokens';
import { StyledButton } from '../styles/StyledButtons';
import { StyledCardContent, StyledSectionCard } from '../styles/styles';
import GlobalSearchField from './GlobalSearchField';
import {
  associatedTypeLabelMap,
  getInitiatorTypeLabel,
  getInitiatorTypesForSearch,
  getModelTypeLabel,
  getSelectedModelTypes,
  mapToSearchTypes,
  modelTypeFilterOptions,
  toggleModelTypeFilter,
} from './activityLogPageFilterHelpers';
import { ActivityLogPageFilters } from './activityLogPageUtils';

interface ActivityLogPageFilterPanelProps {
  showFilterPanel: boolean;
  filters: ActivityLogPageFilters;
  tone: keyof typeof Palette;
  initiatedBySearchResults: GlobalSearchResult[];
  associatedModelSearchResults: GlobalSearchResult[];
  linkedModelSearchResults: GlobalSearchResult[];
  initiatorTypeAnchorEl: HTMLElement | null;
  associatedTypeAnchorEl: HTMLElement | null;
  linkedTypeAnchorEl: HTMLElement | null;
  onFilterChange: (field: keyof ActivityLogPageFilters, value: string | string[]) => void;
  onGlobalSearchChange: (
    field: 'initiatedByExternalId' | 'associatedModelExternalId' | 'linkedModelExternalId',
    results: GlobalSearchResult[],
  ) => void;
  onInitiatorTypeAnchorChange: (el: HTMLElement | null) => void;
  onAssociatedTypeAnchorChange: (el: HTMLElement | null) => void;
  onLinkedTypeAnchorChange: (el: HTMLElement | null) => void;
  onClearFilters: () => void;
}

const ActivityLogPageFilterPanel: React.FC<ActivityLogPageFilterPanelProps> = ({
  showFilterPanel,
  filters,
  tone,
  initiatedBySearchResults,
  associatedModelSearchResults,
  linkedModelSearchResults,
  initiatorTypeAnchorEl,
  associatedTypeAnchorEl,
  linkedTypeAnchorEl,
  onFilterChange,
  onGlobalSearchChange,
  onInitiatorTypeAnchorChange,
  onAssociatedTypeAnchorChange,
  onLinkedTypeAnchorChange,
  onClearFilters,
}) => {
  const initiatorTypesForSearch = getInitiatorTypesForSearch(filters.initiatedByType);
  const initiatorTypeLabel = getInitiatorTypeLabel(filters.initiatedByType);

  const selectedAssociatedTypes = getSelectedModelTypes(filters.associatedModelType);
  const associatedTypeLabel = getModelTypeLabel(selectedAssociatedTypes);
  const associatedTypesForSearch = mapToSearchTypes(selectedAssociatedTypes);

  const selectedLinkedTypes = getSelectedModelTypes(filters.linkedModelType);
  const linkedTypeLabel = getModelTypeLabel(selectedLinkedTypes);
  const linkedTypesForSearch = mapToSearchTypes(selectedLinkedTypes);

  const prefixButtonSx = {
    textTransform: 'none' as const,
    minWidth: 0,
    px: 1,
    color: Palette[tone].text,
    backgroundColor: 'transparent',
    borderRadius: 0,
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    lineHeight: 1.2,
    '&:hover': { backgroundColor: 'transparent' },
  };

  return (
    <Collapse in={showFilterPanel}>
      <StyledSectionCard
        tone={tone}
        sx={{
          mb: 2,
          boxShadow: 'none',
          border: `1px solid ${Palette[tone].border}`,
          borderRadius: RADIUS.sectionChild,
        }}
      >
        <StyledCardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
            }}
          >
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Autocomplete
                multiple
                options={Object.keys(activityLogEventConfig)}
                value={Array.isArray(filters.eventType) ? filters.eventType : []}
                onChange={(_, newValue) => onFilterChange('eventType', newValue as string[])}
                disableCloseOnSelect
                forcePopupIcon={false}
                filterSelectedOptions
                getOptionLabel={(key) =>
                  activityLogEventConfig[key as keyof typeof activityLogEventConfig]?.label ||
                  (key as string)
                }
                renderOption={(props, key) => {
                  const cfg = activityLogEventConfig[key as keyof typeof activityLogEventConfig];
                  return (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span style={{ width: 18, display: 'inline-block' }}>
                          {cfg?.icon ?? ''}
                        </span>
                        <span>{cfg?.label ?? (key as string)}</span>
                      </Box>
                    </li>
                  );
                }}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((eventKey, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    const cfg =
                      activityLogEventConfig[eventKey as keyof typeof activityLogEventConfig];
                    return (
                      <Chip
                        key={key}
                        {...tagProps}
                        size="small"
                        label={`${cfg?.icon ?? ''} ${cfg?.label ?? eventKey}`}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Event Type"
                    placeholder="Search event types..."
                  />
                )}
              />
            </Box>

            <Box>
              <GlobalSearchField
                label="Initiator"
                placeholder="Search for initiators..."
                value={initiatedBySearchResults}
                onChange={(results) => onGlobalSearchChange('initiatedByExternalId', results)}
                types={initiatorTypesForSearch}
                leftPrefix={
                  <>
                    <Button
                      size="small"
                      onClick={(e) => onInitiatorTypeAnchorChange(e.currentTarget)}
                      endIcon={<ExpandMoreIcon />}
                      sx={prefixButtonSx}
                    >
                      {initiatorTypeLabel}
                    </Button>
                    <Menu
                      anchorEl={initiatorTypeAnchorEl}
                      open={Boolean(initiatorTypeAnchorEl)}
                      onClose={() => onInitiatorTypeAnchorChange(null)}
                    >
                      <MenuItem onClick={() => onFilterChange('initiatedByType', '')}>Any</MenuItem>
                      <MenuItem onClick={() => onFilterChange('initiatedByType', 'ADMIN_USER')}>
                        Admin User
                      </MenuItem>
                      <MenuItem onClick={() => onFilterChange('initiatedByType', 'CONTACT')}>
                        Contact
                      </MenuItem>
                    </Menu>
                  </>
                }
                tone={tone}
              />
            </Box>

            <Box>
              <GlobalSearchField
                label="Actioned Model"
                placeholder="Search for actioned models..."
                value={associatedModelSearchResults}
                onChange={(results) => onGlobalSearchChange('associatedModelExternalId', results)}
                types={associatedTypesForSearch}
                disabled={associatedTypesForSearch.length === 0}
                leftPrefix={
                  <>
                    <Button
                      size="small"
                      onClick={(e) => onAssociatedTypeAnchorChange(e.currentTarget)}
                      endIcon={<ExpandMoreIcon />}
                      sx={prefixButtonSx}
                    >
                      {associatedTypeLabel}
                    </Button>
                    <Menu
                      anchorEl={associatedTypeAnchorEl}
                      open={Boolean(associatedTypeAnchorEl)}
                      onClose={() => onAssociatedTypeAnchorChange(null)}
                    >
                      <MenuItem
                        onClick={() =>
                          onFilterChange(
                            'associatedModelType',
                            toggleModelTypeFilter(selectedAssociatedTypes, '__ANY__'),
                          )
                        }
                      >
                        <Checkbox size="small" checked={selectedAssociatedTypes.length === 0} />
                        Any
                      </MenuItem>
                      {modelTypeFilterOptions.map((opt) => (
                        <MenuItem
                          key={opt}
                          onClick={() =>
                            onFilterChange(
                              'associatedModelType',
                              toggleModelTypeFilter(selectedAssociatedTypes, opt),
                            )
                          }
                        >
                          <Checkbox size="small" checked={selectedAssociatedTypes.includes(opt)} />
                          {associatedTypeLabelMap[opt]}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                }
                tone={tone}
              />
            </Box>

            <Box>
              <GlobalSearchField
                label="Linked Model"
                placeholder="Search for linked models..."
                value={linkedModelSearchResults}
                onChange={(results) => onGlobalSearchChange('linkedModelExternalId', results)}
                types={linkedTypesForSearch}
                disabled={linkedTypesForSearch.length === 0}
                leftPrefix={
                  <>
                    <Button
                      size="small"
                      onClick={(e) => onLinkedTypeAnchorChange(e.currentTarget)}
                      endIcon={<ExpandMoreIcon />}
                      sx={prefixButtonSx}
                    >
                      {linkedTypeLabel}
                    </Button>
                    <Menu
                      anchorEl={linkedTypeAnchorEl}
                      open={Boolean(linkedTypeAnchorEl)}
                      onClose={() => onLinkedTypeAnchorChange(null)}
                    >
                      <MenuItem
                        onClick={() =>
                          onFilterChange(
                            'linkedModelType',
                            toggleModelTypeFilter(selectedLinkedTypes, '__ANY__'),
                          )
                        }
                      >
                        <Checkbox size="small" checked={selectedLinkedTypes.length === 0} />
                        Any
                      </MenuItem>
                      {modelTypeFilterOptions.map((opt) => (
                        <MenuItem
                          key={opt}
                          onClick={() =>
                            onFilterChange(
                              'linkedModelType',
                              toggleModelTypeFilter(selectedLinkedTypes, opt),
                            )
                          }
                        >
                          <Checkbox size="small" checked={selectedLinkedTypes.includes(opt)} />
                          {associatedTypeLabelMap[opt]}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                }
                tone={tone}
              />
            </Box>

            <Box>
              <TextField
                fullWidth
                size="small"
                label="Start Date"
                type="datetime-local"
                value={filters.startDate}
                onChange={(e) => onFilterChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Box>
              <TextField
                fullWidth
                size="small"
                label="End Date"
                type="datetime-local"
                value={filters.endDate}
                onChange={(e) => onFilterChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Stack direction="row" spacing={1}>
                <StyledButton
                  variant="outlined"
                  size="small"
                  onClick={onClearFilters}
                  startIcon={<ClearIcon />}
                >
                  Clear
                </StyledButton>
              </Stack>
            </Box>
          </Box>
        </StyledCardContent>
      </StyledSectionCard>
    </Collapse>
  );
};

export default ActivityLogPageFilterPanel;
